import type { User } from './user.type'
import type { Customer } from './customer.type'
import type { SceneContext } from './scene-context.type'
import type { ApiErrorDetails } from './api-error.type'

export const ConversationLogRole = {
  system: 'system',
  user: 'user',
  assistant: 'assistant',
} as const
export type ConversationLogRole = keyof typeof ConversationLogRole

export type ChatMessage = {
  id: number
  conversationId: number
  chatTransactionId: number
  role: ConversationLogRole
  text: string
  data?: ChatMessageAdditionalData
  createdAt: Date
  updatedAt: Date
  chatTransaction?: ChatTransaction
}

export type ChatMessageAdditionalData = {
  // userによるリクエスト
  customerAccountNumber?: number
  customerAccountName?: string
  branchOrgId?: number
  attachments?: UploadedResult[]
  sceneContext?: SceneContext
  clientDate?: string // 送信時点の端末ローカル日付（YYYY-MM-DD）
  // 質問文はユーザーに見えるので、ErrorAssist はリクエスト・レスポンスの中身をこちらで渡す
  errorDetails?: ApiErrorDetails
  // salesAgentによる仕事結果
  savedOrderTicketId?: number
  referencedOrderTicketId?: number
  outputReport?: OutputReport
  // dashboard シーンで Sales Agent が挙げた顧客の一覧。1 message に複数載る
  customerPickups?: CustomerPickup[]
  // maguchiAgent (Iris) の各 propose 種別が、この message に pending 提案として
  // 紐付いている場合 true。種別ごとに対応する GET /maguchi-agent/proposed-* endpoint を
  // 叩いて accept UI を出す。
  hasProposedSettingChanges?: boolean
  hasProposedItemPreferenceChanges?: boolean
  hasProposedTaskCreations?: boolean
  hasProposedTaskUpdates?: boolean
  hasProposedHeadsupDecisions?: boolean
  hasProposedFreeNoteCreation?: boolean
}
export type OutputReport = {
  // line-level (ユーザが入力した or 画像/PDFから抽出した１行のテキスト)
  order_row_total_count: number
  order_row_success_count: number
  order_row_todo_count: number
  order_row_ignore_count: number
  // item-level (実際にOrderする商品。追加フィールドのためOptional。)
  order_item_total_count: number
  order_item_todo_count: number
  order_item_success_count: number
  help_request_message: string
}
// 応答に添える顧客一覧。customers の順で表示する
export type CustomerPickup = {
  title: string
  customers: CustomerPickupCustomer[]
}

export type CustomerPickupCustomer = Pick<Customer, 'id' | 'account_number' | 'account_name'>

export type UploadedResult = {
  key: string
  originalName: string
  fileType: 'image' | 'file'
}
export const ChatTransactionStatus = {
  queued: 'queued',
  in_progress: 'in_progress',
  failed: 'failed',
  completed: 'completed',
  expired: 'expired',
  skipped: 'skipped',
} as const
export type ChatTransactionStatus = keyof typeof ChatTransactionStatus

export type ChatTransaction = {
  id: number
  conversationId: number
  moderationFlagged: boolean
  status: ChatTransactionStatus
  inProgressLabel: string
  workingMemory: string
  processingStartedAt?: Date
  processingCompletedAt?: Date
  createdAt: Date
  updatedAt: Date
}

export type ActiveTxResponse = {
  activeTx: ChatTransaction | null
  newMessages: ChatMessage[]
}

export type Conversation = {
  id: number
  userId: number
  user: User
  title: string
  agent: ConversationAgentAll
  createdAt: Date
  updatedAt: Date
}

export const CONVERSATION_TITLE_MAX_LENGTH = 80

// DB 保存用の全集合。
export const ConversationAgentAll = {
  salesAgent: 'salesAgent',
  // API エラー時の自動診断用。サーバー側では salesAgent と同一パイプラインで実行される
  errorAssist: 'errorAssist',
  orderEntryWorkflow: 'orderEntryWorkflow',
  analyzeAgent: 'analyzeAgent', // deprecated: 単体の会話は廃止
  marketAgent: 'marketAgent',
  maguchiAgent: 'maguchiAgent',
  maguchiWorkflow: 'maguchiWorkflow',
} as const
export type ConversationAgentAll = keyof typeof ConversationAgentAll

// 現役仕様。
export const ConversationAgent = {
  salesAgent: ConversationAgentAll.salesAgent,
  errorAssist: ConversationAgentAll.errorAssist,
  orderEntryWorkflow: ConversationAgentAll.orderEntryWorkflow,
  marketAgent: ConversationAgentAll.marketAgent,
  maguchiAgent: ConversationAgentAll.maguchiAgent,
  maguchiWorkflow: ConversationAgentAll.maguchiWorkflow,
} as const
export type ConversationAgent = keyof typeof ConversationAgent

export type ConversationDetail = Conversation & {
  chatTransactions: ChatTransaction[]
  chatMessages: ChatMessage[]
}

export const ConversationAgentFilter = {
  ...ConversationAgent,
  all: 'all',
} as const
export type ConversationAgentFilter = keyof typeof ConversationAgentFilter

export type GetHistoryQuery = {
  page: number
  limit: number
  agent: ConversationAgentFilter
  customerId?: number
}
export type RunStep = {
  id: number
  toolName: string
  input: unknown
  output: unknown
  createdAt: string
}

export type GetConversationsQuery = GetHistoryQuery & {
  userId?: number
}

// GET /conversations/maguchi-agents の latestMessage（ChatMessage のサブセット）
export type MaguchiAgentLatestMessage = Pick<ChatMessage, 'id' | 'role' | 'text' | 'createdAt'>

// 同 latestTx（ChatTransaction のサブセット。処理 status）
export type MaguchiAgentLatestTx = Pick<
  ChatTransaction,
  'id' | 'status' | 'inProgressLabel' | 'createdAt'
>

export type MaguchiAgentConversationStatus = {
  conversationId: number
  latestMessage: MaguchiAgentLatestMessage | null
  latestTx: MaguchiAgentLatestTx | null
  exitAt: Date | null // null = 稼働中
}

// customer サブセット（swagger MaguchiAgentCustomerDto）
export type MaguchiAgentCustomer = Pick<
  Customer,
  'id' | 'party_name' | 'account_name' | 'account_number'
>

export type MaguchiAgentCustomerGroup = {
  customer: MaguchiAgentCustomer
  conversations: MaguchiAgentConversationStatus[]
}

export type MaguchiAgentStatusListResponse = {
  items: MaguchiAgentCustomerGroup[]
}
